import React, { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Star } from "lucide-react";
import { api } from "../../lib/api";
import { Kpi, RangePicker, ExportButtons, Breakdown, money } from "../../components/ReportKit";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

export default function MyReportsPage() {
  const [days, setDays] = useState(90);
  const [r, setR] = useState(null);
  useEffect(() => { setR(null); api.get(`/reports/me?days=${days}`).then((x) => setR(x.data)).catch(() => setR(false)); }, [days]);

  return (
    <div className="max-w-6xl space-y-6" data-testid="my-reports-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Business</p>
          <h1 className="font-serif font-light text-4xl">Reports</h1>
          <p className="text-sm text-[var(--muted)] mt-1">Leads, bookings, revenue and how couples find you. Track deals in Contacts → Clients.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center"><RangePicker days={days} setDays={setDays} /><ExportButtons path={`/reports/me/export?days=${days}`} testid="my-report-export" /></div>
      </div>
      {r === null && <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />}
      {r === false && <p className="text-sm text-red-600">Couldn't load your report.</p>}
      {r && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi testid="kpi-leads" label="New leads" value={r.leads.new_clients + r.leads.new_conversations} sub={[plural(r.leads.new_clients, "client"), plural(r.leads.new_conversations, "chat"), plural(r.leads.connection_requests, "request")].join(" · ")} />
            <Kpi testid="kpi-bookings" label="Bookings" value={r.bookings.booked + r.bookings.completed} sub={`${plural(r.bookings.agreements_signed, "agreement")} signed`} />
            <Kpi testid="kpi-revenue" label="Payments received" value={money(r.revenue.received)} sub={`${money(r.revenue.outstanding)} outstanding`} />
            <Kpi testid="kpi-views" label="Profile views" value={r.audience.profile_views} sub={`${plural(r.audience.followers, "follower")} (+${r.audience.new_followers})`} />
          </div>
          <div className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid="my-report-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-3">Views & leads per day</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={r.series} margin={{ left: -20, right: 8 }}>
                  <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8A96B" stopOpacity={0.45} /><stop offset="100%" stopColor="#C8A96B" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#eee6d8" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" stroke="#C8A96B" fill="url(#gv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="leads" stroke="#111111" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Breakdown testid="my-report-pipeline" title="Client pipeline" data={r.leads.pipeline} />
            <Breakdown testid="my-report-revenue" title="Revenue" data={{ booked_value: r.revenue.booked_value, received: r.revenue.received, outstanding: r.revenue.outstanding }} fmt={money} />
            <div className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid="my-report-reviews">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2">Reviews & ratings</p>
              <p className="font-serif text-3xl flex items-center gap-2">{r.reviews.average || "–"} <Star className="w-5 h-5 fill-[var(--gold)] text-[var(--gold)]" /></p>
              <p className="text-xs text-[var(--muted)]">{r.reviews.count} reviews</p>
              <div className="mt-3 space-y-2">{r.reviews.recent.map((x, i) => <p key={i} className="text-sm"><b>{x.reviewer_name || "Couple"}</b> · {x.rating}★ <span className="text-[var(--muted)]">{x.comment}</span></p>)}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
